import React, { useState, useEffect } from 'react';
import { 
  Calendar,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Eye,
  Search,
  RefreshCw
} from 'lucide-react';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  orderBy,
  doc,
  updateDoc,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';
import toast from 'react-hot-toast';

const TeacherAttendanceReport = ({ coachingId }) => {
  const { currentUser, userProfile } = useRealtime();


  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [refreshing, setRefreshing] = useState(false);


  useEffect(() => {
    if (coachingId) {
      loadTeachersWithAttendance();
    }
  }, [coachingId, selectedMonth]);

  const loadTeachersWithAttendance = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      // Load teachers
      const teachersQuery = query(
        collection(db, 'users'),
        where('role', '==', 'teacher'),
        where('coachingId', '==', coachingId)
      );
      
      const teachersSnapshot = await getDocs(teachersQuery);
      const teachersList = [];
      
      for (const teacherDoc of teachersSnapshot.docs) {
        const teacherData = { id: teacherDoc.id, ...teacherDoc.data() };
        
        // Calculate attendance for selected month
        const attendanceData = await calculateMonthlyAttendance(teacherData.id, selectedMonth);
        
        teachersList.push({
          ...teacherData,
          attendance: attendanceData
        });
      }
      setTeachers(teachersList);
    } catch (error) {
      console.error('Error loading teachers:', error);
      toast.error('Failed to load teacher data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const parseDateValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      return new Date(value + 'T00:00:00');
    }
    if (value?.toDate) {
      return value.toDate();
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  };

  const calculateMonthlyAttendance = async (teacherId, monthYear) => {
    try {
  
      let year, month;
      if (/^\d{4}-\d{2}$/.test(monthYear)) {
        [year, month] = monthYear.split('-');
      } else {
        const monthDate = new Date(monthYear);
        if (!isNaN(monthDate.getTime())) {
          year = monthDate.getFullYear().toString();
          month = (monthDate.getMonth() + 1).toString().padStart(2, '0');
        } else {
          console.warn('calculateMonthlyAttendance - invalid monthYear', monthYear);
          return {
            totalDays: 0,
            workingDays: 0,
            presentDays: 0,
            absentDays: 0,
            halfDays: 0,
            leaveDays: 0,
            attendancePercentage: 0,
            markedDays: 0
          };
        }
      }

      const normalizedYear = parseInt(year, 10);
      const normalizedMonth = parseInt(month, 10);
      const startDate = new Date(normalizedYear, normalizedMonth - 1, 1, 0, 0, 0, 0);
      const endDate = new Date(normalizedYear, normalizedMonth, 0, 23, 59, 59, 999);
      const totalDaysInMonth = endDate.getDate();

      let workingDays = 0;
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        if (d.getDay() !== 0) {
          workingDays++;
        }
      }

      const attendanceQuery = query(
        collection(db, 'teacherDailyAttendance'),
        where('teacherId', '==', teacherId),
        where('coachingId', '==', coachingId)
      );

      const attendanceSnapshot = await getDocs(attendanceQuery);


      let presentDays = 0;
      let absentDays = 0;
      let halfDays = 0;
      let markedDays = 0;

      attendanceSnapshot.forEach(doc => {
        const attendance = doc.data();



        if (attendance.coachingId !== coachingId) return;

        const attendanceDate = parseDateValue(attendance.date);
        if (!attendanceDate) return;

        if (attendanceDate >= startDate && attendanceDate <= endDate) {
          markedDays++;
          switch (attendance.status) {
            case 'present':
              presentDays++;
              break;
            case 'absent':
              absentDays++;
              break;
            case 'half_day':
              halfDays++;
              break;
          }
        }
      });


      if (markedDays === 0) {

        const legacyQuery = query(
          collection(db, 'teacherAttendance'),
          where('coachingId', '==', coachingId)
        );
        const legacySnapshot = await getDocs(legacyQuery);

        legacySnapshot.forEach(doc => {
          const attendance = doc.data();
          const attendanceDate = parseDateValue(attendance.date);
          if (!attendanceDate) return;
          if (attendanceDate < startDate || attendanceDate > endDate) return;

          const teacherRecord = (attendance.teachers || []).find(t => String(t.teacherId) === String(teacherId));
          if (!teacherRecord) return;

          markedDays++;
          switch (teacherRecord.status) {
            case 'present':
              presentDays++;
              break;
            case 'absent':
              absentDays++;
              break;
            case 'half_day':
              halfDays++;
              break;
          }
        });
      }

      if (markedDays === 0) {
        const leaveQuery = query(
          collection(db, 'teacherLeaveRequests'),
          where('teacherId', '==', teacherId),
          where('status', '==', 'approved')
        );
        
        const leaveSnapshot = await getDocs(leaveQuery);
        let leaveDays = 0;
        let leaveHalfDays = 0;
        
        leaveSnapshot.forEach(doc => {
          const leave = doc.data();
          const leaveStart = parseDateValue(leave.startDate);
          const leaveEnd = parseDateValue(leave.endDate);
          if (!leaveStart || !leaveEnd) return;
          
          // Check if leave overlaps with selected month
          if (leaveStart <= endDate && leaveEnd >= startDate) {
            const overlapStart = new Date(Math.max(leaveStart.getTime(), startDate.getTime()));
            const overlapEnd = new Date(Math.min(leaveEnd.getTime(), endDate.getTime()));
            
            const daysDiff = Math.ceil((overlapEnd - overlapStart) / (1000 * 60 * 60 * 24)) + 1;
            
            if (leave.leaveType === 'half_day') {
              leaveHalfDays += daysDiff;
            } else {
              leaveDays += daysDiff;
            }
          }
        });
        
        // If no attendance records exist, default to absent unless there is approved leave
        if (leaveDays + (leaveHalfDays * 0.5) === 0) {
          presentDays = 0;
          absentDays = workingDays;
          halfDays = 0;
        } else {
          presentDays = workingDays - leaveDays - (leaveHalfDays * 0.5);
          absentDays = leaveDays + (leaveHalfDays * 0.5);
          halfDays = leaveHalfDays;
        }
      }
      
      const effectivePresentDays = presentDays + (halfDays * 0.5);
      const calculatedAbsentDays = Math.max(0, workingDays - effectivePresentDays);
      const attendancePercentage = workingDays > 0 ? Math.round((effectivePresentDays / workingDays) * 100) : 0;
      
      return {
        totalDays: totalDaysInMonth,
        workingDays,
        presentDays: Math.max(0, presentDays),
        absentDays: calculatedAbsentDays,
        halfDays,
        leaveDays: 0, // Not used in new system
        attendancePercentage,
        markedDays
      };
    } catch (error) {
      console.error('Error calculating attendance:', error);
      return {
        totalDays: 0,
        workingDays: 0,
        presentDays: 0,
        absentDays: 0,
        halfDays: 0,
        leaveDays: 0,
        attendancePercentage: 0,
        markedDays: 0
      };
    }
  };

  const calculateNetSalary = (teacher) => {
    const baseSalary = teacher.teacherProfile?.salary?.baseSalary || 0;
    const bonuses = teacher.teacherProfile?.salary?.bonuses || 0;
    const deductions = teacher.teacherProfile?.salary?.deductions || 0;
    const totalNetSalary = baseSalary + bonuses - deductions;
    const attendancePercentage = teacher.attendance?.attendancePercentage || 0;
    
    // Calculate salary based on attendance
    const salaryBasedOnAttendance = (totalNetSalary * attendancePercentage) / 100;
    
    return Math.round(salaryBasedOnAttendance);
  };

  const markSalaryAsPaid = async (teacherId) => {
    try {
      setProcessingPayment(true);
      
      const teacher = teachers.find(t => t.id === teacherId);
      const netSalary = calculateNetSalary(teacher);
      
      // Create salary payment record
      const paymentData = {
        teacherId,
        teacherName: teacher.displayName || teacher.email,
        teacherEmail: teacher.email,
        coachingId,
        month: selectedMonth,
        baseSalary: teacher.teacherProfile?.salary?.baseSalary || 0,
        attendancePercentage: teacher.attendance?.attendancePercentage || 0,
        presentDays: teacher.attendance?.presentDays || 0,
        absentDays: teacher.attendance?.absentDays || 0,
        halfDays: teacher.attendance?.halfDays || 0,
        netSalary,
        bonuses: teacher.teacherProfile?.salary?.bonuses || 0,
        deductions: teacher.teacherProfile?.salary?.deductions || 0,
        status: 'paid',
        paidAt: serverTimestamp(),
        paidBy: currentUser?.uid,
        createdAt: serverTimestamp()
      };
      
      await addDoc(collection(db, 'teacherSalaryPayments'), paymentData);
      
      // Update teacher's last payment info
      await updateDoc(doc(db, 'users', teacherId), {
        'teacherProfile.lastPayment': {
          month: selectedMonth,
          amount: netSalary,
          paidAt: serverTimestamp()
        },
        updatedAt: serverTimestamp()
      });
      
      toast.success('Salary marked as paid successfully');
      loadTeachersWithAttendance(); // Reload to update UI
      
    } catch (error) {
      console.error('Error marking salary as paid:', error);
      toast.error('Failed to mark salary as paid');
    } finally {
      setProcessingPayment(false);
    }
  };

  const getPreviousSettlements = async (teacherId) => {
    try {
      const settlementsQuery = query(
        collection(db, 'teacherSalaryPayments'),
        where('teacherId', '==', teacherId),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(settlementsQuery);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error loading settlements:', error);
      return [];
    }
  };

  const filteredTeachers = teachers.filter(teacher => {
    const searchLower = searchTerm.toLowerCase();
    return (
      teacher.displayName?.toLowerCase().includes(searchLower) ||
      teacher.email?.toLowerCase().includes(searchLower) ||
      teacher.teacherProfile?.subject?.toLowerCase().includes(searchLower)
    );
  });

  const getMonthName = (monthYear) => {
    const [year, month] = monthYear.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <FileText className="w-6 h-6 mr-2 text-blue-600" />
              Teacher Attendance & Salary Report
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              View teacher attendance and manage salary payments for {getMonthName(selectedMonth)}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search teachers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
            <button
              onClick={() => loadTeachersWithAttendance(true)}
              disabled={refreshing}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
              title="Refresh attendance data"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>

          </div>
        </div>


      </div>

      {/* Teachers Report */}
      <div className="p-6">
        {filteredTeachers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Teachers Found
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              No teachers match your search criteria for the selected month.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Teacher</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Working Days</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Present</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Absent</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Half Days</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Attendance %</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Net Salary</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">This Month Salary</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-900 dark:text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTeachers.map((teacher) => {
                  const netSalary = calculateNetSalary(teacher);
                  const isCurrentMonthPaid = teacher.teacherProfile?.lastPayment?.month === selectedMonth;
                  
                  return (
                    <tr key={teacher.id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="py-4 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                            <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {teacher.displayName || teacher.email}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {teacher.teacherProfile?.subject || 'No subject'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-gray-900 dark:text-white">
                        <div className="flex items-center justify-center space-x-1" title="Working days in month (excluding Sundays)">
                          <span>{teacher.attendance?.workingDays || 0}</span>
                          {teacher.attendance?.markedDays > 0 && (
                            <span className="w-2 h-2 bg-green-500 rounded-full" title={`${teacher.attendance.markedDays} days marked`}></span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {teacher.attendance?.presentDays || 0}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                          <XCircle className="w-3 h-3 mr-1" />
                          {teacher.attendance?.absentDays || 0}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                          <Clock className="w-3 h-3 mr-1" />
                          {teacher.attendance?.halfDays || 0}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex items-center justify-center">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            (teacher.attendance?.attendancePercentage || 0) >= 90 
                              ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400'
                              : (teacher.attendance?.attendancePercentage || 0) >= 75
                              ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400'
                              : 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400'
                          }`}>
                            <span className="text-sm font-semibold">
                              {teacher.attendance?.attendancePercentage || 0}%
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-gray-900 dark:text-white">
                        ₹{((teacher.teacherProfile?.salary?.baseSalary || 0) + (teacher.teacherProfile?.salary?.bonuses || 0) - (teacher.teacherProfile?.salary?.deductions || 0)).toLocaleString()}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="font-semibold text-green-600 dark:text-green-400">
                          ₹{netSalary.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => {
                              setSelectedTeacher(teacher);
                              setShowDetailModal(true);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {!isCurrentMonthPaid ? (
                            <button
                              onClick={() => markSalaryAsPaid(teacher.id)}
                              disabled={processingPayment}
                              className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm transition-colors"
                            >
                              Mark Paid
                            </button>
                          ) : (
                            <span className="px-3 py-1 bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 rounded-lg text-sm">
                              Paid
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>



      {/* Detail Modal */}
      {showDetailModal && selectedTeacher && (
        <TeacherDetailModal
          teacher={selectedTeacher}
          month={selectedMonth}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedTeacher(null);
          }}
          getPreviousSettlements={getPreviousSettlements}
          calculateNetSalary={calculateNetSalary}
        />
      )}
    </div>
  );
};

// Teacher Detail Modal Component
const TeacherDetailModal = ({ teacher, month, onClose, getPreviousSettlements, calculateNetSalary }) => {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSettlements();
  }, [teacher.id]);

  const loadSettlements = async () => {
    try {
      const data = await getPreviousSettlements(teacher.id);
      setSettlements(data);
    } catch (error) {
      console.error('Error loading settlements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMonthName = (monthYear) => {
    const [year, month] = monthYear.split('-');
    return new Date(year, month - 1).toLocaleDateString('en-US', { 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const netSalary = calculateNetSalary(teacher);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {teacher.displayName || teacher.email} - {getMonthName(month)} Report
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Detailed attendance and salary information
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Month Summary */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {getMonthName(month)} Summary
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {teacher.attendance?.workingDays || 0}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Working Days</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {teacher.attendance?.presentDays || 0}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Present Days</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {teacher.attendance?.absentDays || 0}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Absent Days</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  ₹{netSalary.toLocaleString()}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Net Salary</p>
              </div>
            </div>
          </div>

          {/* Salary Breakdown */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Salary Calculation
            </h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Base Salary:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  ₹{(teacher.teacherProfile?.salary?.baseSalary || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Attendance ({teacher.attendance?.attendancePercentage || 0}%):</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  ₹{Math.round(((teacher.teacherProfile?.salary?.baseSalary || 0) * (teacher.attendance?.attendancePercentage || 0)) / 100).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Bonuses:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  +₹{(teacher.teacherProfile?.salary?.bonuses || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Deductions:</span>
                <span className="font-medium text-red-600 dark:text-red-400">
                  -₹{(teacher.teacherProfile?.salary?.deductions || 0).toLocaleString()}
                </span>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-900 dark:text-white">Net Salary:</span>
                  <span className="font-bold text-green-600 dark:text-green-400">
                    ₹{netSalary.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Previous Settlements */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6">
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Previous Settlements
            </h4>
            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : settlements.length === 0 ? (
              <p className="text-gray-600 dark:text-gray-400 text-center py-4">
                No previous settlements found
              </p>
            ) : (
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {settlements.map((settlement) => (
                  <div key={settlement.id} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {getMonthName(settlement.month)}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Attendance: {settlement.attendancePercentage}% • 
                        Present: {settlement.presentDays} days
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-green-600 dark:text-green-400">
                        ₹{settlement.netSalary?.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {settlement.paidAt?.toDate?.()?.toLocaleDateString() || 'Date not available'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeacherAttendanceReport;